import { getMoodsReport, MoodReport } from './MoodStatsLogic';
import { EmotionsAndTopicsReport, getEmotionsAndTopicsReport } from './EmotionStatsLogic';
import { getPageTotalWritingTimeInSeconds, Page, PagesMap, wordsCount } from '../../../Models';
import { enumerateDaysInPeriod, getPageId, moveToPrevPeriod, Period } from '../Models';
import moment from 'moment';

export interface SingleValueReport {
  readonly total?: number;
  readonly diff: number;
}

export interface StatsReport {
  readonly period: Period;
  readonly moodReports: ReadonlyArray<MoodReport>;
  readonly emotionsReport: EmotionsAndTopicsReport;
  readonly wordsReport: SingleValueReport;
  readonly timeReport: SingleValueReport;
  readonly pagesReport: SingleValueReport;
  readonly avgTimeReport: SingleValueReport;
}

export const buildInsightsReport = (period: Period, pages: PagesMap): StatsReport => {
  const currentPeriodPages = selectPages(period, pages).filter(p => p.createdAt !== undefined);
  const prevPeriodPages = selectPages(moveToPrevPeriod(period), pages).filter(p => p.createdAt !== undefined);

  const isDayPeriod = period.startTime === period.endTime;
  const emptyReport: SingleValueReport = { diff: 0 };

  return {
    period,
    moodReports: getMoodsReport(period, pages),
    emotionsReport: getEmotionsAndTopicsReport(period, pages),
    avgTimeReport: isDayPeriod ? emptyReport : getAvgWritingTimeReport(currentPeriodPages, prevPeriodPages),
    pagesReport: isDayPeriod ? emptyReport : getTotalPagesCountReport(currentPeriodPages, prevPeriodPages),
    timeReport: getTotalTimeReport(currentPeriodPages, prevPeriodPages),
    wordsReport: getWordsReport(currentPeriodPages, prevPeriodPages),
  };
};

export const writingTimeStringReport = (time: number | undefined): string => {
  if (time === undefined) {
    return '-';
  }
  if (time === 0) {
    return '<1min';
  }
  if (time === 1) {
    return '1 min';
  }
  return `${time} mins`;
};

const selectPages = (period: Period, pagesMap: PagesMap): ReadonlyArray<Page> => {
  const pages: Page[] = [];
  enumerateDaysInPeriod(period, (date: Date) => {
    const pageId = getPageId(moment(date));
    const existingPage = pagesMap[pageId];
    if (existingPage) {
      pages.push(existingPage);
    }
  });

  return pages;
};

const getTotalPagesCountReport = (pages: ReadonlyArray<Page>, prevPages: ReadonlyArray<Page>): SingleValueReport => {
  return {
    total: pages.length,
    diff: getDiffPercentage(pages.length, prevPages.length),
  };
};

export const getWordsCount = (pages: ReadonlyArray<Page>): number | undefined => {
  if (pages.length === 0) {
    return undefined;
  }

  let totalWords = 0;

  for (const page of pages) {
    totalWords += wordsCount(page);
  }

  return totalWords;
};

const getWordsReport = (pages: ReadonlyArray<Page>, prevPeriodPages: ReadonlyArray<Page>): SingleValueReport => {
  const current = getWordsCount(pages);
  const prev = getWordsCount(prevPeriodPages);
  return {
    total: current,
    diff: getDiffPercentage(current, prev),
  };
};

const getTotalWritingTimeInMinutes = (pages: ReadonlyArray<Page>): number | undefined => {
  if (pages.length === 0) {
    return undefined;
  }

  const time =
    pages
      .map(page => {
        return getPageTotalWritingTimeInSeconds(page) / 60;
      })
      .reduce((sum, current) => sum + current, 0) / pages.length;

  return Math.round(time);
};

const getTotalTimeReport = (pages: ReadonlyArray<Page>, prevPeriodPages: ReadonlyArray<Page>): SingleValueReport => {
  const current = getTotalWritingTimeInMinutes(pages);
  const prev = getTotalWritingTimeInMinutes(prevPeriodPages);
  return {
    total: current,
    diff: getDiffPercentage(current, prev),
  };
};

const getAvgWritingTime = (pages: ReadonlyArray<Page>): number => {
  const nonZeroWritingTimePages = pages.filter(page => {
    return Date.parse(page.finishedWritingAt) - Date.parse(page.startedWritingAt) > 0;
  });

  const totalWritingTime = getTotalWritingTimeInMinutes(nonZeroWritingTimePages);

  return totalWritingTime ? Math.round(totalWritingTime / pages.length) : 0;
};

const getAvgWritingTimeReport = (
  currentPeriodPages: ReadonlyArray<Page>,
  prevPeriodPages: ReadonlyArray<Page>,
): SingleValueReport => {
  const current = getAvgWritingTime(currentPeriodPages);
  const prev = getAvgWritingTime(prevPeriodPages);

  return {
    total: current,
    diff: getDiffPercentage(current, prev),
  };
};

const getDiffPercentage = (currentValue: number | undefined, prevValue: number | undefined): number => {
  if (currentValue === undefined || (prevValue === 0 && currentValue === 0)) {
    return 0;
  } else if (prevValue === 0 || prevValue === undefined) {
    return 1000;
  } else {
    const diff = Math.round(((prevValue - currentValue) / prevValue) * -100);
    return diff <= 1000 ? diff : 1000;
  }
};



// WEBPACK FOOTER //
// ./src/containers/Stats/Logic/Reports.ts